import { useQuery } from "@apollo/client";
import { Drawer, Typography } from "@mui/material";
import { useState } from "react";
import { isAuthorizationError } from "utils/errorUtils";

import { TenantWasteFacilityProvider } from "../Facilities/Facility/TenantWasteFacilityProvider";
import { WasteStreamAccessDenied } from "../Facilities/Facility/WasteStreamDetails/WasteStreamAccessDenied";
import { WasteStreamDetailsPanel } from "../Facilities/Facility/WasteStreamDetails/WasteStreamDetailsModal";
import { WasteStreamDetailsModalSkeleton } from "../Facilities/Facility/WasteStreamDetails/WasteStreamDetailsSkeleton";
import { WASTE_STREAM_DRAWER_WIDTH } from "../Facilities/Facility/WasteStreamDetails/wasteStreamDrawerWidth";
import { ManifestDetailDrawer } from "../Manifests/ManifestDetailDrawer";
import { GET_TENANT_STREAM_META_QUERY } from "./api";
import { defaultShipDateEnd, defaultShipDateStart } from "./defaultShipDateRange";

/**
 * A stream's details for a page that names a stream without listing them
 * (containers), with no drawer of its own. Mount it only while shown, so a
 * line item opened from one stream doesn't reopen with the next.
 *
 * The line-item drawer is held in state rather than the URL: the host page's
 * route has no slot for it, and the details' default would push the line's id
 * into whatever optional param the host route has.
 */
export function WasteStreamPanel({
  tenantId,
  wasteStreamId,
  onClose,
  showCloseButton = true,
}: {
  tenantId: string;
  wasteStreamId: string;
  onClose: () => void;
  /** Off when the host draws its own close, e.g. beside a breadcrumb trail. */
  showCloseButton?: boolean;
}) {
  const [lineItemId, setLineItemId] = useState<string | null>(null);
  const { data, error } = useQuery(GET_TENANT_STREAM_META_QUERY, {
    variables: { id: wasteStreamId },
  });
  const facilityId = data?.wasteStream?.facilityId;

  return (
    <>
      {facilityId && (
        <TenantWasteFacilityProvider
          facilityId={facilityId}
          startDate={defaultShipDateStart}
          endDate={defaultShipDateEnd}
        >
          <WasteStreamDetailsPanel
            onClose={onClose}
            wasteStreamId={wasteStreamId}
            buildLineItemHref={(id) => `/o/${tenantId}/waste/streams/${wasteStreamId}/${id}`}
            onLineItemClick={setLineItemId}
            showCloseButton={showCloseButton}
          />
        </TenantWasteFacilityProvider>
      )}
      {!facilityId && isAuthorizationError(error) && <WasteStreamAccessDenied onClose={onClose} />}
      {!facilityId && error && !isAuthorizationError(error) && (
        <Typography sx={{ p: 3 }}>Something went wrong loading this waste stream.</Typography>
      )}
      {!facilityId && !error && (
        <WasteStreamDetailsModalSkeleton showCloseButton={showCloseButton} />
      )}
      <ManifestDetailDrawer
        lineItemId={lineItemId ?? undefined}
        open={!!lineItemId}
        onClose={() => setLineItemId(null)}
      />
    </>
  );
}

/** `WasteStreamPanel` in a drawer of its own, over whatever page opened it. */
export function WasteStreamDrawer({
  tenantId,
  wasteStreamId,
  onClose,
}: {
  tenantId: string;
  wasteStreamId: string;
  onClose: () => void;
}) {
  return (
    <Drawer
      anchor="right"
      open
      onClose={onClose}
      sx={{ "& .MuiDrawer-paper": { width: WASTE_STREAM_DRAWER_WIDTH } }}
    >
      <WasteStreamPanel tenantId={tenantId} wasteStreamId={wasteStreamId} onClose={onClose} />
    </Drawer>
  );
}
